import React, {FC, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {SupplierUser} from './core/_models'
import {getUsers, deleteUser, deleteSelectedUsers, getUserById} from './core/_requests'
import {useAuth} from '../../../modules/auth'
import {UsersListHeader} from './components/header/UsersListHeader'
import {UsersListToolbar} from './components/header/UsersListToolbar'
import {UsersTable} from './table/UsersTable'
import {UserEditModal} from './user-edit-modal/UserEditModal'
import {UsersListPagination} from './components/pagination/UsersListPagination'
import {UsersListLoading} from './components/loading/UsersListLoading'

const UsersList: FC = () => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [users, setUsers] = useState<SupplierUser[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState<SupplierUser | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [itemsPerPage] = useState(10)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await getUsers(currentUser?.token, intl.locale)
      // Handle both old response structure (users) and new structure (data)
      const usersData = response.data || (response as any).users || []
      const totalCount = response.total || usersData.length
      setUsers(usersData)
      setTotalUsers(totalCount)
    } catch (error) {
      console.error('Error fetching users:', error)
      setUsers([])
      setTotalUsers(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser?.token) {
      fetchUsers()
    }
  }, [currentUser?.token, currentPage, searchTerm])

  const handleEditUser = async (user: SupplierUser) => {
    try {
      setLoading(true)
      // Fetch complete user data including permissions
      const completeUser = await getUserById(user.supplier_user_id, currentUser?.token, intl.locale)
      if (completeUser) {
        setEditingUser(completeUser)
        setShowEditModal(true)
      }
    } catch (error) {
      console.error('Error fetching user details:', error)
      alert(intl.formatMessage({id: 'USERS.FETCH_ERROR'}))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId: number) => {
    if (window.confirm(intl.formatMessage({id: 'USERS.DELETE_CONFIRMATION'}))) {
      try {
        await deleteUser(userId, currentUser?.token, intl.locale)
        await fetchUsers() // Refresh the list
      } catch (error) {
        console.error('Error deleting user:', error)
        alert(intl.formatMessage({id: 'USERS.DELETE_ERROR'}))
      }
    }
  }

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return
    
    if (window.confirm(intl.formatMessage({id: 'USERS.BULK_DELETE_CONFIRMATION'}))) {
      try {
        await deleteSelectedUsers(selectedUsers, currentUser?.token, intl.locale)
        setSelectedUsers([])
        await fetchUsers() // Refresh the list
      } catch (error) {
        console.error('Error deleting users:', error)
        alert(intl.formatMessage({id: 'USERS.BULK_DELETE_ERROR'}))
      }
    }
  }

  const handleAddUser = () => {
    setEditingUser(null)
    setShowEditModal(true)
  }

  const handleModalClose = () => {
    setShowEditModal(false)
    setEditingUser(null)
    fetchUsers() // Refresh the list
  }

  const handleSearch = (term: string) => {
    setSearchTerm(term)
    setCurrentPage(1) // Reset to first page when searching
  }

  const totalPages = Math.ceil(totalUsers / itemsPerPage)

  return (
    <>
      <KTCard>
        <UsersListHeader />
        <KTCardBody className='py-4'>
          <UsersListToolbar
            onAddUser={handleAddUser}
            onBulkDelete={handleBulkDelete}
            onSearch={handleSearch}
            selectedUsersCount={selectedUsers.length}
            totalUsers={totalUsers}
          />
          
          {loading ? (
            <UsersListLoading />
          ) : users.length === 0 ? (
            <div className='text-center py-10'>
              <h4 className='text-muted'>{intl.formatMessage({id: 'USERS.NO_USERS_FOUND'})}</h4>
              <p className='text-muted'>{intl.formatMessage({id: 'USERS.NO_USERS_DESCRIPTION'})}</p>
            </div>
          ) : (
            <>
              <UsersTable
                users={users}
                selectedUsers={selectedUsers}
                onSelectionChange={setSelectedUsers}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
              />
              
              {totalPages > 1 && (
                <UsersListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </KTCardBody>
      </KTCard>

      {showEditModal && (
        <UserEditModal
          user={editingUser}
          onClose={handleModalClose}
        />
      )}
    </>
  )
}

export {UsersList}